import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

const ACTION_CODES: Record<string, string> = {
  branchTaxSummaryInfo: '42kxhl',
  withHoldTaxSummaryInfo: '35n2lz',
};

@Component({
    selector: 'gtw-branchtax-summary-info',
    templateUrl: './branchtax-summary-info.component.html',
    styleUrls: ['./branchtax-summary-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BranchtaxSummaryInfoComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  baseURLs: any = { 'admin': '', 'api': '', 'custom': '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '' };

  filterParams: any = {};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};
  loading = false;
  private modalName = '';
  private fetched = false;

  minimise = false;
  modalTitle = "Account Details";

  summaryInfo: any[] = [];

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen: any = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService.USER_SETTINGS = header.userSettingsData || {};
    this.appService.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] || {}) : row;

    this.modalName = activeScreen?.data?.modal_name
      || activeScreen?.data?.modalName
      || '';

    const rawSummary =
      activeScreen?.data?.summaryInfo || activeScreen?.data?.summary_info;
    this.summaryInfo = Array.isArray(rawSummary) ? rawSummary : [];
  }

  init(): void {
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.setupSummaryData();
    }
  }

  private setupSummaryData(): void {
    if (this.summaryInfo && this.summaryInfo.length > 0) {
      this.cdr.detectChanges();
      return;
    }
    if (this.rowData.summaryInfo) {
      this.summaryInfo = this.rowData.summaryInfo;
    } else if (this.rowData.SUMMARY_INFO) {
      this.summaryInfo = this.rowData.SUMMARY_INFO;
    } else if (this.rowData.jsonObject && Array.isArray(this.rowData.jsonObject)) {
      this.summaryInfo = this.rowData.jsonObject;
    }

    if (this.summaryInfo.length === 0) {
      this.fetchSummaryInfo();
    } else {
      this.cdr.detectChanges();
    }
  }

  private fetchSummaryInfo(): void {
    if (this.fetched || this.loading) return;

    const actionCode = ACTION_CODES[this.modalName] || ACTION_CODES['branchTaxSummaryInfo'];
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    if (!baseUrl) return;

    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';

    const specificParams: any = {
      sso_id: ssoId,
      scenario: this.filterParams.scenario || this.globalService?.globalParams?.scenario || '',
      leid: this.rowData.LEID || '',
      cdr_no: this.rowData.CDR_NO || '',
      reporting_period: this.rowData.REPORTING_PERIOD || '',
      me_code: this.rowData.ME_CODE || '',
    };
    if (this.modalName === 'withHoldTaxSummaryInfo') {
      specificParams.tax_year = this.filterParams.tax_year || this.globalService?.globalParams?.tax_year || '';
    }

    const params = _.merge({}, this.filterParams, specificParams);

    this.loading = true;
    this.fetched = true;
    this.cdr.detectChanges();

    this.subs.add(
      this.jsonObjectFactory.getJSONObj(
        `${baseUrl}/loadJsonObject?action_code=${actionCode}`,
        params,
        this.globalService,
        null,
        null
      ).subscribe({
        next: (res: any) => {
          this.loading = false;
          if (Array.isArray(res.jsonObject)) {
            this.summaryInfo = res.jsonObject;
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  numberWithCommas(value: any): string {
    if (value == null || value === undefined) return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
}
